import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { HorusMessageGeneralComponent } from '@shared/components/domain/horus-message-general/horus-message-general.component';
import { HorusLoadingComponent } from '@shared/components/domain/horus-loading/horus-loading.component';
import { AvailableDatePipe } from '@shared/pipes/available-date.pipe';
import { TenantUrlUtilService } from '@shared/services/tenant-url-util.service';
import { ServiceRescheduleData } from '@features/service-actions/models/service-action.model';
import { ServiceRescheduleState } from '@features/service-actions/store/service-reschedule.state';
import { ServiceRescheduleRequest } from '@features/service-actions/models/service-reschedule.model';
import { serviceRescheduleMeta, SERVICE_TIME_RANGES, DEFAULT_TIME_RANGE } from '@features/service-actions/constants/service-reschedule.constants';
import { getAriaTicketUrl } from 'app/core/utils/url';
import { ServiceRescheduleConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { HorusBtnDirective } from '../../../../shared/directives/horus-btn.directive';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

@Component({
  selector: 'app-service-reschedule-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatListModule,
    MatButtonToggleModule,
    HorusMessageGeneralComponent,
    HorusLoadingComponent,
    AvailableDatePipe,
    HorusBtnDirective,
  ],
  templateUrl: './service-reschedule-dialog.component.html',
  styleUrl: './service-reschedule-dialog.component.scss',
})
export class ServiceRescheduleDialogComponent {
  private readonly dialogRef = inject(
    MatDialogRef<ServiceRescheduleDialogComponent>
  );
  private readonly dialog = inject(MatDialog);
  readonly serviceRescheduleState = inject(ServiceRescheduleState);
  private readonly fb = inject(FormBuilder);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);

  readonly data = inject<ServiceRescheduleData>(MAT_DIALOG_DATA);
  readonly rescheduleServiceForm: FormGroup;
  readonly timeRanges = SERVICE_TIME_RANGES;

  readonly tenantId = computed(() => this.tenantUrlUtil.currentTenant());
  readonly agent = computed(() => this.tenantUrlUtil.agent());
  readonly searchValue = computed(() => this.tenantUrlUtil.documentValue());
  readonly dateRange = computed(() => this.tenantUrlUtil.dateRange());

  constructor() {
    dayjs.extend(customParseFormat);
    this.rescheduleServiceForm = this.fb.group({
      rescheduleTime: [DEFAULT_TIME_RANGE, Validators.required],
      rescheduleDate: [null, Validators.required],
      observations: ['', Validators.required],
    });
  }

  private convertDateToISO(dateStr: string): string {
    return dayjs(dateStr, 'DD/MM/YYYY').format('YYYY-MM-DD');
  }

  ngOnInit(): void {
    if (this.data.availableReschedule && this.data.serviceId) {
      this.serviceRescheduleState.serviceId.set(this.data.serviceId);
      this.serviceRescheduleState.serviceItemId.set(this.data.serviceItemId);
    }
  }

  rescheduleService(): void {
    if (
      !this.rescheduleServiceForm.valid ||
      !this.tenantId() ||
      !this.data.serviceId
    ) {
      return;
    }

    const dateValue = this.rescheduleServiceForm.value.rescheduleDate;
    const timeValue = this.rescheduleServiceForm.value.rescheduleTime;
    const formattedDate = this.convertDateToISO(dateValue);

    const payload: ServiceRescheduleRequest = {
      date: formattedDate,
      childId: this.data.serviceItemId,
      osId: this.data.serviceId,
      user: this.agent(),
      turn: timeValue,
      channel: serviceRescheduleMeta.channel,
      motiveId: serviceRescheduleMeta.motiveId,
      observations: this.rescheduleServiceForm.value.observations,
      type: serviceRescheduleMeta.type,
    };

    this.dialog.open(ServiceRescheduleConfirmDialogComponent, {
      height: '342px',
      width: '427px',
      data: {
        tenantId: this.tenantId()!,
        request: payload,
        serviceId: this.data.serviceId,
        serviceItemId: this.data.serviceItemId
      },
      disableClose: false,
      restoreFocus: false,
      panelClass: 'confirm-dialog-panel',
    });
  }

  getAriaUrl(): string {
    const currentTenant = this.tenantId();
    if (!currentTenant) return '';

    return getAriaTicketUrl(currentTenant, this.data.serviceId, 'SERVICE');
  }

  redirectToAria(): void {
    const url = this.getAriaUrl();
    if (url) {
      window.open(url, '_blank');
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
